import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import KoobButton from '@koob/ui/src/components/KoobButton';
import KoobConfirmationModal from '@koob/ui/src/components/KoobConfirmationModal';
import KoobTripDesignerHeader from '@koob/ui/src/components/KoobTripDesignerHeader';
import { useTripFilters } from '../hooks/useTripStoreSelectors';
import { useTripTotal } from '../hooks/useTripTotal';
import { useShouldWarnBeforeExit } from '../hooks/useHasUnsavedChanges';

interface TripHeaderProps {
  onTripEdit?: () => void;
  onExit: () => void;
  versionSelector?: React.ReactNode;
  // TO-only: a "chat with my DMC" button, rendered before Exit. DMC passes
  // nothing.
  extraActions?: React.ReactNode;
  tripLabel: string;
  editLabel: string;
  priceLabel: string;
  paxLabel: string;
  misMatchCurrencyLabel: string;
  totalDetailsLabel: string;
  // KD-3744 — the per-pax notice, appended under the trip total details.
  pricePerPaxNotice: string;
  exitLabel: string;
  exitUnsavedTitle: string;
  exitUnsavedDescription: string;
  exitUnsavedStayLabel: string;
  exitUnsavedConfirmLabel: string;
  formatPrice: (amount: number, currency: string) => string;
}

// KD-3391: the trip header lives in the app navbar; the data (versions,
// reload) lives in the page, so the strip is portaled into the navbar slot.
export default function TripHeader({
  onTripEdit,
  onExit,
  versionSelector,
  extraActions,
  tripLabel,
  editLabel,
  priceLabel,
  paxLabel,
  misMatchCurrencyLabel,
  totalDetailsLabel,
  pricePerPaxNotice,
  exitLabel,
  exitUnsavedTitle,
  exitUnsavedDescription,
  exitUnsavedStayLabel,
  exitUnsavedConfirmLabel,
  formatPrice,
}: TripHeaderProps) {
  const tripFilters = useTripFilters();
  const { priceLoading, globalTripPrice, globalTripCurrency, participants } =
    useTripTotal();
  const hasUnsavedChanges = useShouldWarnBeforeExit();
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const [navbarSlot, setNavbarSlot] = useState<HTMLElement | null | undefined>(
    undefined,
  );
  useEffect(() => {
    setNavbarSlot(document.getElementById('koob-navbar-trip-designer-slot'));
  }, []);

  const mismatch = globalTripCurrency === 'mismatch';
  const hasPrice = !mismatch && globalTripCurrency != null;

  if (navbarSlot === undefined) {
    return null;
  }

  const header = (
    <KoobTripDesignerHeader
      tripLabel={tripLabel}
      tripName={tripFilters?.programName}
      onTripEdit={onTripEdit}
      editLabel={editLabel}
      versionSelector={versionSelector}
      priceLoading={priceLoading}
      priceLabel={priceLabel}
      price={
        hasPrice ? formatPrice(globalTripPrice, globalTripCurrency) : undefined
      }
      pricePerPax={
        hasPrice && participants > 0
          ? `${formatPrice(globalTripPrice / participants, globalTripCurrency)} / ${paxLabel}`
          : undefined
      }
      priceError={mismatch ? misMatchCurrencyLabel : undefined}
      priceTip={
        !mismatch
          ? `${totalDetailsLabel}<br /><br />${pricePerPaxNotice}`
          : undefined
      }
      actions={
        <>
          {extraActions}

          <KoobButton
            onClick={() =>
              hasUnsavedChanges ? setExitConfirmOpen(true) : onExit()
            }
          >
            {exitLabel}
          </KoobButton>

          {/* KD-3656: leaving the composer drops what was not saved as draft. */}
          <KoobConfirmationModal
            open={exitConfirmOpen}
            setOpen={setExitConfirmOpen}
            title={exitUnsavedTitle}
            description={exitUnsavedDescription}
            cancelLabel={exitUnsavedStayLabel}
            confirmLabel={exitUnsavedConfirmLabel}
            onConfirm={onExit}
            danger
          >
            {null}
          </KoobConfirmationModal>
        </>
      }
    />
  );

  return navbarSlot ? (
    createPortal(header, navbarSlot)
  ) : (
    <div className="mt-4">{header}</div>
  );
}
